/**
 * PlayerStatsTableView - View for player stats module
 *
 * Displays competition statistics, transfer history, and injury history
 * with tabbed navigation.
 * "View all" opens a full-screen overlay on mobile
 * and a centered modal on desktop. The season dropdown header
 * remains visible even when no statistics are available for the selected
 * season, allowing users to switch seasons without navigating away.
 * SE-5570: the season selector renders through the shared `SeasonSelectDropdown`
 * (on the SelectDropdown molecule / FixedPositionDropdownShell) instead of the
 * retired gluestack Menu atom. Inside the "View all" overlay the season
 * dropdown mounts its panel in its own RN Modal stacked above the overlay —
 * this stacked-Modal behavior is intentional (SE-5570).
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import React, { memo } from "react";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";

import type {
  CompetitionStatRow,
  InjuryRow,
  SeasonOption,
  StatsTab,
  TabConfig,
  TransferRow,
} from "@/features/player-detail/types";

import FullScreenOverlay from "@/components/organisms/shared/FullScreenOverlay";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Text } from "@/components/atoms/Text";
import type { TextVariant } from "@/components/atoms/types";

import StatsTabButton from "@/features/player-detail/components/molecules/StatsTabButton";
import SeasonSelectDropdown from "@/features/player-detail/components/molecules/SeasonSelectDropdown";

/**
 * Props for PlayerStatsTableView component
 */
interface PlayerStatsTableViewProps {
  /** Whether dark mode is active */
  isDark: boolean;
  /** Currently active tab */
  activeTab: StatsTab;
  /** Whether data is loading */
  isLoading: boolean;
  /** Competition stats rows (limited) */
  stats: readonly CompetitionStatRow[];
  /** Transfer rows (limited) */
  transfers: readonly TransferRow[];
  /** Injury rows (limited) */
  injuries: readonly InjuryRow[];
  /** All competition stats (for modal) */
  allStats: readonly CompetitionStatRow[];
  /** All transfers (for modal) */
  allTransfers: readonly TransferRow[];
  /** All injuries (for modal) */
  allInjuries: readonly InjuryRow[];
  /** Total row count for current tab */
  totalRowCount: number;
  /** Whether modal is open */
  isModalOpen: boolean;
  /** Whether the screen is mobile sized */
  isSmallScreen: boolean;
  /** Available season options for dropdown */
  seasonOptions: readonly SeasonOption[];
  /** Currently selected season ID */
  selectedSeason: string;
  /** Callback when tab changes */
  onTabChange: (tab: StatsTab) => void;
  /** Callback when View all is pressed */
  onViewAllPress: () => void;
  /** Callback when modal closes */
  onModalClose: () => void;
  /** Invoked with the chosen season ID */
  onSelectSeason: (seasonId: string) => void;
  /** Display label for the selected season (dropdown trigger text) */
  selectedSeasonLabel: string;
  /** Tabs to display — gated by PostHog feature flag in container */
  visibleTabs: readonly TabConfig[];
}

/**
 * Get container classes
 * @param isDark - Dark mode flag
 */
const getContainerClasses = (): string =>
  "flex-1 rounded-md border border-outline-card bg-surface-card p-4";

/**
 * Get header text classes — modal uses higher contrast text
 * against its solid background vs the inline widget's semi-transparent bg
 * @param isDark - Dark mode flag
 * @param isModal - Whether rendered in modal context (SE-4038)
 */
const getHeaderTextClasses = (isDark: boolean, isModal = false): string => {
  const darkTone = isModal ? "text-content-disabled" : "text-surface-strong";
  const lightTone = isModal
    ? "text-content-secondary"
    : "text-content-neutral-strong";
  return isDark ? darkTone : lightTone;
};

/**
 * Get value text classes
 * @param isDark - Dark mode flag
 */
const getValueTextClasses = (isDark: boolean): string =>
  isDark ? "text-white" : "text-content-primary";

/**
 * Stats header parameters
 */
interface StatsHeaderParams {
  readonly isDark: boolean;
  readonly isModal?: boolean;
  readonly seasonOptions: readonly SeasonOption[];
  readonly selectedSeason: string;
  readonly selectedSeasonLabel: string;
  readonly onSelectSeason: (seasonId: string) => void;
}

/**
 * Render stats table header with season dropdown
 * @param params - Header parameters
 * @param params.isDark - Dark mode flag
 * @param params.isModal - Whether rendered in modal context for higher contrast (SE-4038)
 * @param params.seasonOptions - Available seasons
 * @param params.selectedSeason - Selected season ID
 * @param params.selectedSeasonLabel - Display label for the selected season
 * @param params.onSelectSeason - Invoked with the chosen season ID
 */
const renderStatsHeader = ({
  isDark,
  isModal = false,
  seasonOptions,
  selectedSeason,
  selectedSeasonLabel,
  onSelectSeason,
}: StatsHeaderParams) => (
  <Stack
    direction="horizontal"
    className={`justify-between border-b pb-3 ${
      isDark ? "border-outline-emphasis" : "border-outline-default"
    }`}
  >
    <SeasonSelectDropdown
      seasonOptions={seasonOptions}
      selectedSeason={selectedSeason}
      selectedSeasonLabel={selectedSeasonLabel}
      onSelectSeason={onSelectSeason}
    />
    <Stack direction="horizontal" className="gap-8">
      <Text
        variant="caption"
        className={`w-8 text-center ${getHeaderTextClasses(isDark, isModal)}`}
      >
        MP
      </Text>
      <Text
        variant="caption"
        className={`w-8 text-center ${getHeaderTextClasses(isDark, isModal)}`}
      >
        G
      </Text>
      <Text
        variant="caption"
        className={`w-8 text-center ${getHeaderTextClasses(isDark, isModal)}`}
      >
        A
      </Text>
      <Text
        variant="caption"
        className={`w-12 text-center ${getHeaderTextClasses(isDark, isModal)}`}
      >
        Mins
      </Text>
    </Stack>
  </Stack>
);

/**
 * Get the row text style based on row type — both are 14px; only the total row
 * is emphasised (SE-4038). Size and weight travel together in the bundle, so
 * this is a text style rather than a size class plus a weight utility.
 * @param isTotal - Whether this is a total row
 * @returns The text style for the row.
 */
const getStatsRowTextVariant = (isTotal: boolean | undefined): TextVariant =>
  isTotal ? "body-bold" : "body";

/**
 * Get row text colour — non-total rows use the same high-contrast colors as
 * total rows (SE-4038); only the emphasis differs.
 * @param isDark - Dark mode flag
 * @returns The colour class for the row.
 */
const getStatsRowTextClasses = (isDark: boolean): string =>
  isDark ? "text-white" : "text-content-primary";

/**
 * Render a stats row (table format for all rows)
 * @param row - Stat row data
 * @param isDark - Dark mode flag
 */
const renderStatsRow = (row: CompetitionStatRow, isDark: boolean) => {
  const textClasses = getStatsRowTextClasses(isDark);
  const textVariant = getStatsRowTextVariant(row.isTotal);
  const hasMatches = row.matchesPlayed > 0;
  const totalBorderTone = isDark
    ? "border-outline-emphasis"
    : "border-outline-default";
  const rowBorderClasses = row.isTotal ? `border-b ${totalBorderTone}` : "";

  return (
    <Stack
      direction="horizontal"
      key={row.id}
      className={`justify-between py-3 ${rowBorderClasses}`}
    >
      <Text variant={textVariant} className={textClasses}>
        {row.name}
      </Text>
      <Stack direction="horizontal" className="gap-8">
        <Text
          variant={textVariant}
          className={`w-8 text-center ${textClasses}`}
        >
          {hasMatches ? row.matchesPlayed : "—"}
        </Text>
        <Text
          variant={textVariant}
          className={`w-8 text-center ${textClasses}`}
        >
          {hasMatches ? row.goals : "—"}
        </Text>
        <Text
          variant={textVariant}
          className={`w-8 text-center ${textClasses}`}
        >
          {hasMatches ? row.assists : "—"}
        </Text>
        <Text
          variant={textVariant}
          className={`w-12 text-center ${textClasses}`}
        >
          {row.minutes > 0 ? row.minutes : "—"}
        </Text>
      </Stack>
    </Stack>
  );
};

/**
 * Render transfers table header
 * @param isDark - Dark mode flag
 * @param isModal - Whether rendered in modal context (SE-4038)
 */
const renderTransfersHeader = (isDark: boolean, isModal = false) => (
  <Stack direction="horizontal" className="items-center justify-between pb-2">
    <Text
      variant="caption"
      className={`flex-1 ${getHeaderTextClasses(isDark, isModal)}`}
    >
      From
    </Text>
    <Text
      variant="caption"
      className={`flex-1 ${getHeaderTextClasses(isDark, isModal)}`}
    >
      To
    </Text>
    <Text
      variant="caption"
      className={`w-16 text-center ${getHeaderTextClasses(isDark, isModal)}`}
    >
      Fee
    </Text>
    <Text
      variant="caption"
      className={`w-24 text-right ${getHeaderTextClasses(isDark, isModal)}`}
    >
      Date
    </Text>
  </Stack>
);

/**
 * Render a transfer row
 * @param row - Transfer row data
 * @param isDark - Dark mode flag
 * @param isModal - Whether rendered in modal context (SE-4038)
 */
const renderTransferRow = (
  row: TransferRow,
  isDark: boolean,
  isModal = false
) => (
  <Stack
    direction="horizontal"
    key={row.id}
    className="items-center justify-between py-2"
  >
    <Text
      variant="body"
      className={`flex-1 truncate ${getValueTextClasses(isDark)}`}
    >
      {row.from}
    </Text>
    <Text
      variant="body"
      className={`flex-1 truncate ${getValueTextClasses(isDark)}`}
    >
      {row.to}
    </Text>
    <Text
      variant="body"
      className={`w-16 text-center ${getValueTextClasses(isDark)}`}
    >
      {row.fee}
    </Text>
    <Text
      variant="caption"
      className={`w-24 text-right ${getHeaderTextClasses(isDark, isModal)}`}
    >
      {row.date}
    </Text>
  </Stack>
);

/**
 * Render injuries table header
 * @param isDark - Dark mode flag
 * @param isModal - Whether rendered in modal context (SE-4038)
 */
const renderInjuriesHeader = (isDark: boolean, isModal = false) => (
  <Stack direction="horizontal" className="items-center justify-between pb-2">
    <Text
      variant="caption"
      className={`w-24 ${getHeaderTextClasses(isDark, isModal)}`}
    >
      Date
    </Text>
    <Text
      variant="caption"
      className={`flex-1 ${getHeaderTextClasses(isDark, isModal)}`}
    >
      Injury Type
    </Text>
    <Text
      variant="caption"
      className={`w-20 text-right ${getHeaderTextClasses(isDark, isModal)}`}
    >
      Days Missed
    </Text>
  </Stack>
);

/**
 * Render an injury row
 * @param row - Injury row data
 * @param isDark - Dark mode flag
 * @param isModal - Whether rendered in modal context (SE-4038)
 */
const renderInjuryRow = (row: InjuryRow, isDark: boolean, isModal = false) => (
  <Stack
    direction="horizontal"
    key={row.id}
    className="items-center justify-between py-2"
  >
    <Text
      variant="caption"
      className={`w-24 ${getHeaderTextClasses(isDark, isModal)}`}
    >
      {row.date}
    </Text>
    <Text
      variant="body"
      className={`flex-1 truncate ${getValueTextClasses(isDark)}`}
    >
      {row.injuryType}
    </Text>
    <Text
      variant="body"
      className={`w-20 text-right ${getValueTextClasses(isDark)}`}
    >
      {row.daysMissed > 0 ? row.daysMissed : "—"}
    </Text>
  </Stack>
);

/**
 * Render empty state
 * @param isDark - Dark mode flag
 * @param message - Empty state message
 */
const renderEmptyState = (isDark: boolean, message: string) => (
  <Stack className="items-center justify-center py-4">
    <Text variant="caption" className={getHeaderTextClasses(isDark)}>
      {message}
    </Text>
  </Stack>
);

/**
 * Tab content parameters
 */
interface TabContentParams {
  readonly isDark: boolean;
  readonly isLoading: boolean;
  readonly activeTab: StatsTab;
  readonly stats: readonly CompetitionStatRow[];
  readonly transfers: readonly TransferRow[];
  readonly injuries: readonly InjuryRow[];
  readonly seasonOptions: readonly SeasonOption[];
  readonly selectedSeason: string;
  readonly onSelectSeason: (seasonId: string) => void;
  readonly selectedSeasonLabel: string;
}

/**
 * Get content based on active tab
 * @param params - Parameters for tab content
 * @param params.isDark - Whether dark mode is enabled for styling
 * @param params.isLoading - Whether data is still being fetched
 * @param params.activeTab - Currently selected tab identifier
 * @param params.stats - Competition statistics rows to display
 * @param params.transfers - Transfer history rows to display
 * @param params.injuries - Injury history rows to display
 * @param params.seasonOptions - Available seasons for dropdown
 * @param params.selectedSeason - Currently selected season
 * @param params.onSelectSeason - Invoked with the chosen season ID
 * @param params.selectedSeasonLabel - Display label for the selected season
 */
const getTabContent = ({
  isDark,
  isLoading,
  activeTab,
  stats,
  transfers,
  injuries,
  seasonOptions,
  selectedSeason,
  onSelectSeason,
  selectedSeasonLabel,
}: TabContentParams): React.ReactElement => {
  if (isLoading) {
    return renderEmptyState(isDark, "Loading...");
  }

  switch (activeTab) {
    case "stats": {
      const totalRow = stats.find(row => row.isTotal);
      const gameRows = stats.filter(row => !row.isTotal);

      return (
        <Stack>
          {/* Season dropdown header - always visible so users can switch seasons */}
          {renderStatsHeader({
            isDark,
            seasonOptions,
            selectedSeason,
            onSelectSeason,
            selectedSeasonLabel,
          })}
          {stats.length === 0 ? (
            renderEmptyState(isDark, "No statistics available")
          ) : (
            <>
              {totalRow ? renderStatsRow(totalRow, isDark) : null}
              {gameRows.length > 0 ? (
                <ScrollView className="max-h-72" showsVerticalScrollIndicator>
                  {gameRows.map(row => renderStatsRow(row, isDark))}
                </ScrollView>
              ) : null}
            </>
          )}
        </Stack>
      );
    }
    case "transfers":
      return transfers.length > 0 ? (
        <Stack>
          {renderTransfersHeader(isDark)}
          <ScrollView className="max-h-72" showsVerticalScrollIndicator>
            {transfers.map(row => renderTransferRow(row, isDark))}
          </ScrollView>
        </Stack>
      ) : (
        renderEmptyState(isDark, "No transfer history")
      );
    case "injuries":
      return injuries.length > 0 ? (
        <Stack>
          {renderInjuriesHeader(isDark)}
          <ScrollView className="max-h-72" showsVerticalScrollIndicator>
            {injuries.map(row => renderInjuryRow(row, isDark))}
          </ScrollView>
        </Stack>
      ) : (
        renderEmptyState(isDark, "No injury history")
      );
    default:
      return <></>;
  }
};

/**
 * Modal content parameters
 */
interface ModalContentParams {
  readonly isDark: boolean;
  readonly activeTab: StatsTab;
  readonly allStats: readonly CompetitionStatRow[];
  readonly allTransfers: readonly TransferRow[];
  readonly allInjuries: readonly InjuryRow[];
  readonly seasonOptions: readonly SeasonOption[];
  readonly selectedSeason: string;
  readonly onSelectSeason: (seasonId: string) => void;
  readonly selectedSeasonLabel: string;
}

/**
 * Get modal content based on active tab
 * @param params - Parameters for modal content
 * @param params.isDark - Whether dark mode is enabled for styling
 * @param params.activeTab - Currently selected tab identifier
 * @param params.allStats - Complete competition statistics for modal view
 * @param params.allTransfers - Complete transfer history for modal view
 * @param params.allInjuries - Complete injury history for modal view
 * @param params.seasonOptions - Available seasons for dropdown
 * @param params.selectedSeason - Currently selected season
 * @param params.onSelectSeason - Invoked with the chosen season ID
 * @param params.selectedSeasonLabel - Display label for the selected season
 */
const getModalContent = ({
  isDark,
  activeTab,
  allStats,
  allTransfers,
  allInjuries,
  seasonOptions,
  selectedSeason,
  onSelectSeason,
  selectedSeasonLabel,
}: ModalContentParams): React.ReactElement => {
  switch (activeTab) {
    case "stats": {
      // Separate Total row (sticky) from game rows (scrollable)
      const totalRow = allStats.find(row => row.isTotal);
      const gameRows = allStats.filter(row => !row.isTotal);

      return (
        <Stack>
          {/* Sticky: Header + Total row */}
          {renderStatsHeader({
            isDark,
            isModal: true,
            seasonOptions,
            selectedSeason,
            onSelectSeason,
            selectedSeasonLabel,
          })}
          {totalRow ? renderStatsRow(totalRow, isDark) : null}

          {/* Game rows - modal body handles scrolling */}
          {gameRows.map(row => renderStatsRow(row, isDark))}
        </Stack>
      );
    }
    case "transfers":
      return (
        <Stack>
          {renderTransfersHeader(isDark, true)}
          {allTransfers.map(row => renderTransferRow(row, isDark, true))}
        </Stack>
      );
    case "injuries":
      return (
        <Stack>
          {renderInjuriesHeader(isDark, true)}
          {allInjuries.map(row => renderInjuryRow(row, isDark, true))}
        </Stack>
      );
    default:
      return <></>;
  }
};

/**
 * PlayerStatsTableView renders the stats module with tabs
 * @param props - View props
 * @param props.isDark - Whether dark mode is active
 * @param props.activeTab - Currently active tab
 * @param props.isLoading - Whether data is loading
 * @param props.stats - Competition stats rows
 * @param props.transfers - Transfer rows
 * @param props.injuries - Injury rows
 * @param props.allStats - All stats for modal
 * @param props.allTransfers - All transfers for modal
 * @param props.allInjuries - All injuries for modal
 * @param props.totalRowCount - Total rows for current tab
 * @param props.isModalOpen - Whether modal is open
 * @param props.isSmallScreen - Whether to use full-screen overlay (mobile) vs centered modal (desktop)
 * @param props.seasonOptions - Available seasons for dropdown
 * @param props.selectedSeason - Currently selected season
 * @param props.onTabChange - Tab change callback
 * @param props.onViewAllPress - View all callback
 * @param props.onModalClose - Modal close callback
 * @param props.onSelectSeason - Invoked with the chosen season ID
 * @param props.selectedSeasonLabel - Display label for the selected season
 * @param props.visibleTabs - Tabs to display, gated by PostHog feature flag in container
 */
const PlayerStatsTableView = ({
  isDark,
  activeTab,
  isLoading,
  stats,
  transfers,
  injuries,
  allStats,
  allTransfers,
  allInjuries,
  totalRowCount,
  isModalOpen,
  isSmallScreen,
  seasonOptions,
  selectedSeason,
  onTabChange,
  onViewAllPress,
  onModalClose,
  onSelectSeason,
  selectedSeasonLabel,
  visibleTabs,
}: PlayerStatsTableViewProps) => (
  <>
    <Box className={getContainerClasses()}>
      <Stack className="gap-3">
        {/* Tab bar and View all */}
        <Stack direction="horizontal" className="items-center justify-between">
          <Stack direction="horizontal" className="gap-2">
            {visibleTabs.map(tab => (
              <StatsTabButton
                key={tab.id}
                tab={tab}
                isActive={activeTab === tab.id}
                isDark={isDark}
                onPress={onTabChange}
              />
            ))}
          </Stack>
          <Pressable
            onPress={onViewAllPress}
            className="flex-row items-center gap-1"
          >
            <Text variant="body" className="text-content-subtle">
              View all ({totalRowCount})
            </Text>
            <Text variant="caption" className="text-content-subtle">
              ↗
            </Text>
          </Pressable>
        </Stack>

        {/* Tab content */}
        <Box className="pt-2">
          {getTabContent({
            isDark,
            isLoading,
            activeTab,
            stats,
            transfers,
            injuries,
            seasonOptions,
            selectedSeason,
            onSelectSeason,
            selectedSeasonLabel,
          })}
        </Box>
      </Stack>
    </Box>

    {/* View All — full-screen overlay on mobile, centered modal on desktop */}
    {isSmallScreen ? (
      <FullScreenOverlay
        isOpen={isModalOpen}
        onClose={onModalClose}
        title="Player Statistics"
      >
        <Stack className="gap-3">
          <Stack direction="horizontal" className="gap-2">
            {visibleTabs.map(tab => (
              <StatsTabButton
                key={tab.id}
                tab={tab}
                isActive={activeTab === tab.id}
                isDark={isDark}
                onPress={onTabChange}
              />
            ))}
          </Stack>
          {getModalContent({
            isDark,
            activeTab,
            allStats,
            allTransfers,
            allInjuries,
            seasonOptions,
            selectedSeason,
            onSelectSeason,
            selectedSeasonLabel,
          })}
        </Stack>
      </FullScreenOverlay>
    ) : (
      <Modal isOpen={isModalOpen} onClose={onModalClose} size="md">
        <ModalBackdrop />
        <ModalContent className="max-h-2/3 max-w-96 overflow-hidden border border-outline-subtle bg-surface-base">
          <ModalHeader className="border-b border-outline-subtle pb-4">
            <Text
              variant="title"
              className={isDark ? "text-white" : "text-content-primary"}
            >
              Player Statistics
            </Text>
            <Pressable onPress={onModalClose}>
              <Text
                variant="body-strong"
                className="text-needs-semantic-primary-500"
              >
                Close
              </Text>
            </Pressable>
          </ModalHeader>
          <Stack
            direction="horizontal"
            className="gap-2 border-b border-outline-subtle px-0 py-3"
          >
            {visibleTabs.map(tab => (
              <StatsTabButton
                key={tab.id}
                tab={tab}
                isActive={activeTab === tab.id}
                isDark={isDark}
                onPress={onTabChange}
              />
            ))}
          </Stack>
          <ModalBody className="my-0 pt-4">
            {getModalContent({
              isDark,
              activeTab,
              allStats,
              allTransfers,
              allInjuries,
              seasonOptions,
              selectedSeason,
              onSelectSeason,
              selectedSeasonLabel,
            })}
          </ModalBody>
        </ModalContent>
      </Modal>
    )}
  </>
);

PlayerStatsTableView.displayName = "PlayerStatsTableView";

export default memo(PlayerStatsTableView);
